import { useEffect, useState } from 'react';
import { ApiError, blobUrl, download, request } from '../api';
import { dateTime, Field, Loadable, money, Stat, todayIso, useLoad } from '../ui';
import type { StoreRef } from './Store';

function useAction() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>, ok = 'Saved') => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg({ ok: true, text: ok });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const view = msg && <div className={`alert ${msg.ok ? 'ok' : 'error'}`} role="status">{msg.text}</div>;
  return { busy, run, view };
}

function QrThumb({ storeId, tableId }: { storeId: string; tableId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    blobUrl(`/v1/stores/${storeId}/qr/tables/${encodeURIComponent(tableId)}/qr.png`).then((u) => setSrc((url = u))).catch(() => setSrc(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [storeId, tableId]);
  return src ? <img src={src} alt="" width={72} height={72} /> : <div className="qr-placeholder" />;
}

/** Guest ratings of served QR orders at this outlet (last 30 days). */
function GuestRatings({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/qr-feedback?days=30`), [store.id]);
  return (
    <section className="panel" aria-labelledby="guest-ratings">
      <h3 id="guest-ratings">Guest ratings (30 days)</h3>
      <Loadable state={state} empty={(d: any) => d.summary.count === 0}>
        {(d: any) => (
          <>
            <div className="stats">
              <Stat label="Average rating" value={d.summary.average === null ? 'n/a' : `${d.summary.average.toFixed(1)} / 5`} sub={`${d.summary.count} rating${d.summary.count === 1 ? '' : 's'}`} />
              <Stat label="5 and 4 stars" value={d.summary.r5 + d.summary.r4} sub={`${d.summary.r3} three · ${d.summary.r2 + d.summary.r1} two or less`} />
            </div>
            <table>
              <thead><tr><th>When</th><th>Rating</th><th>Table</th><th>Order</th><th>Comment</th></tr></thead>
              <tbody>{d.recent.map((f: any, i: number) => (
                <tr key={i}><td>{dateTime(f.created_at)}</td><td aria-label={`${f.rating} out of 5`}>{'★'.repeat(f.rating)}</td><td>{f.table_label || 'n/a'}</td><td>{f.order_number ?? 'n/a'}</td><td>{f.comment || <span className="muted">n/a</span>}</td></tr>
              ))}</tbody>
            </table>
          </>
        )}
      </Loadable>
    </section>
  );
}

export function QrOrdering({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/qr`), [store.id]);
  const requests = useLoad(() => request('GET', `/v1/stores/${store.id}/qr/requests`), [store.id]);
  const analytics = useLoad(() => request('GET', `/v1/stores/${store.id}/qr/analytics?from=${todayIso(-6)}&to=${todayIso()}`), [store.id]);
  const act = useAction();
  const [draft, setDraft] = useState<Record<string, any> | null>(null);

  return (
    <Loadable state={state}>
      {(res: any) => {
        const s = draft ?? res.settings ?? {};
        const set = (k: string, v: unknown) => setDraft({ ...s, [k]: v });
        const save = () =>
          act.run(async () => {
            const body = Object.fromEntries(
              ['enabled', 'public_enabled', 'lan_enabled', 'accept_mode', 'offline_policy', 'allow_repeat_orders', 'request_ttl_seconds', 'max_items_per_order', 'max_quantity_per_item', 'max_open_requests_per_table', 'require_customer_name', 'payment_mode', 'welcome_message'].map((k) => [k, s[k]]).filter(([, v]) => v !== undefined && v !== null),
            );
            await request('POST', `/v1/stores/${store.id}/qr/settings`, body);
            setDraft(null);
            setTimeout(state.reload, 1500);
          }, 'Sent to the POS');
        return (
          <>
            {!res.connected && <div className="alert warn">The POS is not connected right now. You can view and print codes, but changes need the POS online (or make them on the POS directly).</div>}
            <Loadable state={analytics}>
              {(a: any) => (
                <div className="stats">
                  <Stat label="QR requests (7 days)" value={a.by_status.reduce((n: number, r: any) => n + r.count, 0)} sub={a.by_status.map((r: any) => `${r.count} ${r.status}`).join(' · ')} />
                  <Stat label="QR orders" value={a.qr_orders} sub={money(a.qr_order_value, store.currency)} />
                  <Stat label="Average time to accept" value={a.avg_accept_seconds === null ? 'n/a' : `${Math.round(a.avg_accept_seconds)} s`} />
                </div>
              )}
            </Loadable>
            <GuestRatings store={store} />
            <section className="panel">
              <h3>Settings</h3>
              <div className="form-grid">
                <label className="check"><input type="checkbox" checked={Boolean(s.enabled)} onChange={(e) => set('enabled', e.target.checked)} /> QR ordering on</label>
                <label className="check"><input type="checkbox" checked={Boolean(s.public_enabled)} onChange={(e) => set('public_enabled', e.target.checked)} /> Internet ordering (public link)</label>
                <label className="check"><input type="checkbox" checked={Boolean(s.lan_enabled)} onChange={(e) => set('lan_enabled', e.target.checked)} /> Restaurant Wi-Fi ordering (LAN)</label>
                <label className="check"><input type="checkbox" checked={s.allow_repeat_orders !== false} onChange={(e) => set('allow_repeat_orders', e.target.checked)} /> Allow additional rounds</label>
                <Field label="Accepting orders"><select value={s.accept_mode ?? 'manual'} onChange={(e) => set('accept_mode', e.target.value)}><option value="manual">Staff accept each order</option><option value="auto">Send straight to the kitchen</option></select></Field>
                <Field label="When the POS is offline"><select value={s.offline_policy ?? 'queue'} onChange={(e) => set('offline_policy', e.target.value)}><option value="queue">Queue requests (not accepted until the POS confirms)</option><option value="reject">Refuse new orders</option></select></Field>
                <Field label="Request expires after (seconds)"><input type="number" min={60} max={7200} value={s.request_ttl_seconds ?? 900} onChange={(e) => set('request_ttl_seconds', Number(e.target.value))} /></Field>
                <Field label="Max lines per order"><input type="number" min={1} max={100} value={s.max_items_per_order ?? 30} onChange={(e) => set('max_items_per_order', Number(e.target.value))} /></Field>
                <Field label="Max quantity per item"><input type="number" min={1} max={99} value={s.max_quantity_per_item ?? 20} onChange={(e) => set('max_quantity_per_item', Number(e.target.value))} /></Field>
                <Field label="Max waiting requests per table"><input type="number" min={1} max={50} value={s.max_open_requests_per_table ?? 5} onChange={(e) => set('max_open_requests_per_table', Number(e.target.value))} /></Field>
                <Field label="Payment"><select value={s.payment_mode ?? 'pay_at_counter'} onChange={(e) => set('payment_mode', e.target.value)}><option value="pay_at_counter">Pay at counter / after meal</option><option value="online_optional">Online payment optional</option><option value="online_required">Online payment required</option></select></Field>
                <Field label="Welcome message"><input maxLength={300} value={s.welcome_message ?? ''} onChange={(e) => set('welcome_message', e.target.value)} /></Field>
              </div>
              <button className="btn" disabled={act.busy || !draft} onClick={save}>Save to POS</button>
              {act.view}
            </section>
            <section className="panel">
              <div className="row-between">
                <h3>Table codes</h3>
                <button className="btn small" disabled={!res.tables.length} onClick={() => download(`/v1/stores/${store.id}/qr/print.pdf`, 'table-qr-codes.pdf').catch((e) => alert(e.message))}>Download print-ready PDF</button>
              </div>
              {!res.tables.length && <p className="muted">No tables published yet. Turn QR ordering on (on the POS or above) and the tables appear here.</p>}
              <table>
                <thead><tr><th>QR</th><th>Table</th><th>Link</th><th>Ordering</th><th /></tr></thead>
                <tbody>
                  {res.tables.map((t: any) => (
                    <tr key={t.local_table_id}>
                      <td><QrThumb storeId={store.id} tableId={t.local_table_id} /></td>
                      <td>{t.label}{t.section ? <span className="muted"> · {t.section}</span> : null}</td>
                      <td className="small"><code>{t.url}</code></td>
                      <td>{t.enabled ? 'On' : 'Off'}</td>
                      <td><div className="actions">
                        <button className="btn small secondary" onClick={() => download(`/v1/stores/${store.id}/qr/tables/${encodeURIComponent(t.local_table_id)}/qr.png`, `table-${t.label}.png`)}>PNG</button>
                        <button className="btn small secondary" disabled={act.busy} onClick={() => act.run(() => request('POST', `/v1/stores/${store.id}/qr/tables/${encodeURIComponent(t.local_table_id)}/enabled`, { enabled: !t.enabled }).then(() => setTimeout(state.reload, 1500)), 'Sent to the POS')}>{t.enabled ? 'Turn off' : 'Turn on'}</button>
                        <button className="btn small danger" disabled={act.busy} onClick={() => window.confirm(`Replace the code for table ${t.label}? The printed code stops working.`) && act.run(() => request('POST', `/v1/stores/${store.id}/qr/tables/${encodeURIComponent(t.local_table_id)}/rotate`).then(() => setTimeout(state.reload, 1500)), 'New code issued')}>New code</button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="panel">
              <div className="row-between"><h3>Recent customer requests</h3><button className="btn small secondary" onClick={requests.reload}>Refresh</button></div>
              <Loadable state={requests} empty={(r: any) => !r.requests.length}>
                {(r: any) => (
                  <table>
                    <thead><tr><th>When</th><th>Table</th><th>Status</th><th>Order</th><th>Value</th></tr></thead>
                    <tbody>{r.requests.map((x: any) => <tr key={x.id}><td>{dateTime(x.created_at)}</td><td>{x.table_label}</td><td>{x.status}{x.status_reason ? <span className="muted"> · {x.status_reason}</span> : null}</td><td>{x.pos_order_number ?? 'n/a'}</td><td className="num">{money(Number(x.pos_total ?? x.estimated_subtotal), store.currency)}</td></tr>)}</tbody>
                  </table>
                )}
              </Loadable>
              <p className="muted small">Accepting and rejecting happens on the POS ("QR Ordering" screen), where staff see the kitchen and tables.</p>
            </section>
          </>
        );
      }}
    </Loadable>
  );
}

export function Devices({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/devices`), [store.id]);
  const act = useAction();
  return (
    <Loadable state={state}>
      {(d: any) => (
        <>
          {act.view}
          <section className="panel">
            <h3>POS terminals</h3>
            <table>
              <thead><tr><th>Device</th><th>Version</th><th>Last seen</th><th>Status</th><th /></tr></thead>
              <tbody>{d.pos_devices.map((p: any) => (
                <tr key={p.id}>
                  <td>{p.device_name}<div className="muted small">{p.platform} {p.arch} · {p.install_source}</div></td>
                  <td>{p.app_version}</td><td>{dateTime(p.last_seen_at)}</td>
                  <td>{p.revoked_at ? 'Revoked' : p.connected ? 'Connected' : 'Offline'}</td>
                  <td>{!p.revoked_at && store.role === 'owner' && <button className="btn small danger" onClick={() => window.confirm('Revoke this POS? It will stop syncing until registered again.') && act.run(() => request('POST', `/v1/stores/${store.id}/pos-devices/${p.id}/revoke`).then(state.reload), 'Revoked')}>Revoke</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
          <section className="panel">
            <h3>Paired phones and browsers</h3>
            <table>
              <thead><tr><th>Device</th><th>User</th><th>Paired</th><th>Last seen</th><th /></tr></thead>
              <tbody>{d.mobile_devices.map((m: any) => (
                <tr key={m.id}>
                  <td>{m.device_name} <span className="muted small">{m.platform}</span></td><td>{m.email}</td><td>{dateTime(m.created_at)}</td><td>{m.revoked_at ? 'Revoked' : dateTime(m.last_seen_at)}</td>
                  <td>{!m.revoked_at && <button className="btn small danger" onClick={() => act.run(() => request('POST', `/v1/stores/${store.id}/mobile-devices/${m.id}/revoke`).then(state.reload), 'Signed out')}>Sign out</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        </>
      )}
    </Loadable>
  );
}

export function Sync({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/sync`), [store.id]);
  const alerts = useLoad(() => request('GET', `/v1/stores/${store.id}/alerts`), [store.id]);
  return (
    <Loadable state={state}>
      {(s: any) => (
        <>
          <div className="stats">
            <Stat label="Live connection" value={s.connected ? 'Connected' : 'Offline'} />
            <Stat label="Events (7 days)" value={s.last_7_days.events} sub={`${s.last_7_days.errors} with projection errors`} />
            <Stat label="Last event" value={dateTime(s.last_7_days.last_event_at)} />
          </div>
          {s.devices.map((d: any) => (
            <p key={d.id} className="muted small">{d.device_name}: last seen {dateTime(d.last_seen_at)}{d.last_heartbeat ? ` · reports ${d.last_heartbeat.active_orders ?? 0} open orders` : ''}</p>
          ))}
          <section className="panel">
            <h3>Alerts</h3>
            <Loadable state={alerts} empty={(a: any) => !a.alerts.length}>
              {(a: any) => <ul className="plain">{a.alerts.map((x: any) => <li key={x.id}><span className={`pill ${x.severity}`}>{x.severity}</span> {x.message} <span className="muted small">{dateTime(x.created_at)}{x.resolved_at ? ` · resolved ${dateTime(x.resolved_at)}` : ''}</span></li>)}</ul>}
            </Loadable>
          </section>
          <section className="panel">
            <h3>Recent sync events</h3>
            <table>
              <thead><tr><th>Received</th><th>Type</th><th>Entity</th><th>Result</th></tr></thead>
              <tbody>{s.recent_events.map((e: any) => <tr key={e.event_id}><td>{dateTime(e.received_at)}</td><td>{e.type}</td><td>{e.entity_id ?? ''}</td><td>{e.projection_error ? <span className="error">{e.projection_error}</span> : e.projected_at ? 'Applied' : 'Pending'}</td></tr>)}</tbody>
            </table>
          </section>
        </>
      )}
    </Loadable>
  );
}

export function Team({ orgId, myRole, stores }: { orgId: string; myRole: string; stores: StoreRef[] }) {
  const state = useLoad(() => request('GET', `/v1/orgs/${orgId}/members`), [orgId]);
  const act = useAction();
  const [invite, setInvite] = useState({ email: '', role: 'manager', store_ids: [] as string[] });
  const canManage = myRole === 'owner' || myRole === 'admin';
  return (
    <Loadable state={state}>
      {(d: any) => (
        <>
          {act.view}
          <section className="panel">
            <h3>Members</h3>
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Stores</th><th /></tr></thead>
              <tbody>{d.members.map((m: any) => (
                <tr key={m.id}>
                  <td>{m.name}</td><td>{m.email}</td>
                  <td>{canManage ? (
                    <select value={m.role} onChange={(e) => act.run(() => request('PATCH', `/v1/orgs/${orgId}/members/${m.id}`, { role: e.target.value, store_ids: m.store_ids }).then(state.reload))}>
                      {['owner', 'admin', 'manager', 'viewer'].map((r) => <option key={r}>{r}</option>)}
                    </select>
                  ) : m.role}</td>
                  <td className="small">{m.store_ids.length ? m.store_ids.map((id: string) => stores.find((s) => s.id === id)?.name ?? id).join(', ') : 'All'}</td>
                  <td>{canManage && <button className="btn small danger" onClick={() => window.confirm(`Remove ${m.email}?`) && act.run(() => request('DELETE', `/v1/orgs/${orgId}/members/${m.id}`).then(state.reload), 'Removed')}>Remove</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
          {canManage && (
            <section className="panel">
              <h3>Invite someone</h3>
              <div className="form-grid">
                <Field label="Email"><input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
                <Field label="Role"><select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>{(myRole === 'owner' ? ['admin', 'manager', 'viewer'] : ['manager', 'viewer']).map((r) => <option key={r}>{r}</option>)}</select></Field>
                <Field label="Stores (none selected = all)">
                  <select multiple value={invite.store_ids} onChange={(e) => setInvite({ ...invite, store_ids: [...e.target.selectedOptions].map((o) => o.value) })}>
                    {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              </div>
              <button className="btn" disabled={act.busy || !invite.email} onClick={() => act.run(() => request('POST', `/v1/orgs/${orgId}/invites`, invite).then(() => { setInvite({ email: '', role: 'manager', store_ids: [] }); state.reload(); }), 'Invitation sent')}>Send invitation</button>
              {d.invites.length > 0 && (
                <>
                  <h4>Pending invitations</h4>
                  <ul className="plain">{d.invites.map((i: any) => <li key={i.id}>{i.email} · {i.role} · expires {dateTime(i.expires_at)} <button className="link" onClick={() => act.run(() => request('DELETE', `/v1/orgs/${orgId}/invites/${i.id}`).then(state.reload), 'Invitation withdrawn')}>Withdraw</button></li>)}</ul>
                </>
              )}
            </section>
          )}
        </>
      )}
    </Loadable>
  );
}

export function Audit({ orgId }: { orgId: string }) {
  const [before, setBefore] = useState<number | null>(null);
  const state = useLoad(() => request('GET', `/v1/orgs/${orgId}/audit${before ? `?before=${before}` : ''}`), [orgId, before]);
  return (
    <Loadable state={state}>
      {(d: any) => (
        <>
          <table>
            <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Store</th><th>Target</th></tr></thead>
            <tbody>{d.entries.map((e: any) => <tr key={e.id}><td>{dateTime(e.created_at)}</td><td>{e.actor_email ?? e.actor_type}</td><td>{e.action}</td><td>{e.store_name ?? ''}</td><td className="small">{e.target ?? ''}</td></tr>)}</tbody>
          </table>
          {d.next_before && <button className="btn small secondary" onClick={() => setBefore(d.next_before)}>Older</button>}
        </>
      )}
    </Loadable>
  );
}

export function Account({ onSignedOut }: { onSignedOut: () => void }) {
  const me = useLoad(() => request('GET', '/v1/auth/me'), []);
  const sessions = useLoad(() => request('GET', '/v1/auth/sessions'), []);
  const act = useAction();
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [del, setDel] = useState({ password: '', confirmation: '' });
  return (
    <>
      {act.view}
      <Loadable state={me}>
        {(m: any) => (
          <section className="panel">
            <h3>Profile</h3>
            <p>{m.user.name} · {m.user.email} {m.user.email_verified ? <span className="pill good">verified</span> : <button className="link" onClick={() => act.run(() => request('POST', '/v1/auth/resend-verification'), 'Verification email sent')}>Verify email</button>}</p>
          </section>
        )}
      </Loadable>
      <section className="panel">
        <h3>Change password</h3>
        <div className="form-grid">
          <Field label="Current password"><input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
          <Field label="New password"><input type="password" autoComplete="new-password" minLength={10} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
        </div>
        <button className="btn" disabled={act.busy} onClick={() => act.run(() => request('POST', '/v1/auth/change-password', pw).then(() => setPw({ current_password: '', new_password: '' })), 'Password changed; other sessions were signed out')}>Change password</button>
      </section>
      <section className="panel">
        <h3>Signed-in devices</h3>
        <Loadable state={sessions}>
          {(s: any) => (
            <table>
              <thead><tr><th>Device</th><th>Since</th><th>Last used</th><th /></tr></thead>
              <tbody>{s.sessions.map((x: any) => <tr key={x.family_id}><td>{x.device_name || x.client}{x.current ? <span className="pill">this device</span> : null}<div className="muted small">{x.user_agent.slice(0, 80)}</div></td><td>{dateTime(x.created_at)}</td><td>{dateTime(x.last_used_at)}</td><td>{!x.current && <button className="btn small danger" onClick={() => act.run(() => request('DELETE', `/v1/auth/sessions/${x.family_id}`).then(sessions.reload), 'Signed out')}>Sign out</button>}</td></tr>)}</tbody>
            </table>
          )}
        </Loadable>
      </section>
      <section className="panel danger-zone">
        <h3>Delete account</h3>
        <p className="muted">Deletes your login. Organizations you are the only owner of must be handed over first. Restaurant data is deleted from the POS (Settings → Cloud → Delete cloud data).</p>
        <div className="form-grid">
          <Field label="Password"><input type="password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} /></Field>
          <Field label='Type "DELETE MY ACCOUNT"'><input value={del.confirmation} onChange={(e) => setDel({ ...del, confirmation: e.target.value })} /></Field>
        </div>
        <button className="btn danger" disabled={act.busy || del.confirmation !== 'DELETE MY ACCOUNT'} onClick={() => act.run(() => request('DELETE', '/v1/auth/account', del).then(onSignedOut), 'Account deleted')}>Delete my account</button>
      </section>
    </>
  );
}
